import { useState } from "react";
import { HelpCircle, ShieldCheck, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import useDocTitle from "../lib/useDocTitle";
import { submitContact } from "../lib/api";

const INQUIRY_TYPES = ["General", "Support", "Partnership", "Legal", "Other"];

export default function Contact() {
  useDocTitle(
    "Contact · Lulo Software",
    "Contact Lulo Software LLC for general inquiries, customer support, legal and privacy questions.",
    "contact Lulo Software, support, partnership, legal, privacy",
    "/contact",
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    inquiry_type: "General",
    message: "",
    botcheck: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (form.message.trim().length < 20) e.message = "Message must be at least 20 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || loading) return;
    setLoading(true);
    try {
      await submitContact(form);
      setSuccess(true);
      toast.success("Message received. We'll respond within 24 business hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't send your message. Please try again or email support@lulosoftware.com.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20 max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Contact</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight text-[#0A2540]">
            Contact Lulo Software
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-7">
            We read every message. Use the channel that best matches your inquiry, or send a
            note via the form below.
          </p>
        </div>
      </section>

      {/* Contact methods */}
      <section className="border-b border-slate-200">
        <div className="lulo-container py-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              icon: HelpCircle,
              title: "Customer Support",
              email: "support@lulosoftware.com",
              desc: "For product support, general inquiries, and partnership questions across our portfolio.",
              test: "contact-card-support",
            },
            {
              icon: ShieldCheck,
              title: "Legal & Privacy",
              email: "legal@lulosoftware.com",
              desc: "For legal inquiries, privacy questions, GDPR/CCPA requests, and compliance matters.",
              test: "contact-card-legal",
            },
          ].map((c) => (
            <div key={c.title} data-testid={c.test} className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
                <c.icon size={20} />
              </div>
              <h3 className="mt-5 text-base font-semibold text-[#0A2540]">{c.title}</h3>
              <a
                href={`mailto:${c.email}`}
                className="mt-1 block text-sm text-[#2563EB] hover:text-[#1d4ed8]"
              >
                {c.email}
              </a>
              <p className="mt-3 text-sm text-slate-600 leading-6">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Address + Hours */}
      <section className="border-b border-slate-200 bg-[#F9FAFB]">
        <div className="lulo-container py-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-testid="contact-address" className="rounded-xl border border-slate-200 bg-white p-6 flex items-start gap-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
              <MapPin size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#0A2540]">Mailing Address</h3>
              <p className="mt-2 text-sm text-slate-700 leading-6">
                Lulo Software LLC<br />
                30 N Gould St, Ste R<br />
                Sheridan, WY 82801, USA
              </p>
            </div>
          </div>
          <div data-testid="contact-hours" className="rounded-xl border border-slate-200 bg-white p-6 flex items-start gap-4">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
              <Clock size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#0A2540]">Operating Hours</h3>
              <p className="mt-2 text-sm text-slate-700 leading-6">
                Monday – Friday, 9:00 AM – 6:00 PM India Standard Time (UTC+5:30)<br />
                Email response within 24 business hours.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="bg-white">
        <div className="lulo-container py-16 lg:py-20 max-w-3xl">
          <h2 className="text-2xl font-semibold text-[#0A2540]">Send us a message</h2>
          <p className="mt-2 text-sm text-slate-600">
            We respond to every message within one business day.
          </p>

          {success ? (
            <div
              data-testid="contact-success"
              className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6"
            >
              <h3 className="text-base font-semibold text-emerald-900">Thank you for reaching out.</h3>
              <p className="mt-1 text-sm text-emerald-800">
                We&apos;ll respond within 24 business hours.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="contact-form" className="mt-8 grid grid-cols-1 gap-5">
              {/* Honeypot */}
              <input
                type="text"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                value={form.botcheck}
                onChange={update("botcheck")}
                style={{ position: "absolute", left: "-5000px", height: 0, width: 0, opacity: 0 }}
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-slate-800">Name<span className="text-rose-600">*</span></label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                  />
                  {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-800">Email<span className="text-rose-600">*</span></label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                  />
                  {errors.email && <p className="mt-1 text-xs text-rose-600">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-slate-800">Company <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    data-testid="contact-company"
                    className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-800">Inquiry Type</label>
                  <select
                    value={form.inquiry_type}
                    onChange={update("inquiry_type")}
                    data-testid="contact-inquiry-type"
                    className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                  >
                    {INQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-800">
                  Message<span className="text-rose-600">*</span>{" "}
                  <span className="text-slate-400 font-normal">(min 20 characters)</span>
                </label>
                <textarea
                  required
                  rows={6}
                  minLength={20}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-message"
                  className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
                />
                {errors.message && <p className="mt-1 text-xs text-rose-600">{errors.message}</p>}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center justify-center rounded-md bg-[#0A2540] px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-[#081c30] disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                </button>
                <p className="mt-3 text-xs text-slate-500">
                  By sending this form you agree to our{" "}
                  <a href="/privacy" className="text-[#2563EB] hover:text-[#1d4ed8]">Privacy Policy</a>.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
